import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, CalendarDays, FileText, Plus, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Logo from "../components/Logo";

const TABS = ["overview", "contacts", "bookings", "blog"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [posts, setPosts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ title: "", excerpt: "", content: "", cover_image: "", tags: "", published: true });

  const email = localStorage.getItem("apex_admin_email");

  const loadAll = async () => {
    try {
      const [s, c, b, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/contacts"),
        api.get("/admin/bookings"),
        api.get("/admin/blog"),
      ]);
      setStats(s.data); setContacts(c.data); setBookings(b.data); setPosts(p.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("apex_admin_token");
        nav("/admin/login");
      }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("apex_admin_token")) {
      nav("/admin/login");
      return;
    }
    loadAll();
  }, []);

  const logout = () => {
    localStorage.removeItem("apex_admin_token");
    localStorage.removeItem("apex_admin_email");
    nav("/admin/login");
  };

  const updateContact = async (id, status) => {
    await api.patch(`/admin/contacts/${id}`, { status });
    toast.success("Updated");
    loadAll();
  };
  const updateBooking = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    toast.success("Updated");
    loadAll();
  };

  const createPost = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blog", {
        ...draft,
        tags: draft.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Post created");
      setShowNew(false);
      setDraft({ title: "", excerpt: "", content: "", cover_image: "", tags: "", published: true });
      loadAll();
    } catch {
      toast.error("Failed to create post");
    }
  };
  const deletePost = async (id) => {
    if (!confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  return (
    <main className="min-h-screen bg-secondary/30" data-testid="admin-dashboard">
      <header className="glass-nav sticky top-0 z-40">
        <div className="container-apex h-16 flex items-center justify-between">
          <Logo size={32} />
          <div className="flex items-center gap-4">
            <button onClick={loadAll} className="text-sm font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-2" data-testid="refresh-btn">
              <RefreshCw size={14} /> Refresh
            </button>
            <span className="text-sm text-muted-foreground hidden sm:inline">{email}</span>
            <button onClick={logout} className="text-sm font-semibold inline-flex items-center gap-2 text-foreground hover:text-[#16A34A]" data-testid="logout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-apex py-10">
        <div className="overline">Dashboard</div>
        <h1 className="heading-display text-3xl sm:text-4xl mt-3">Admin console</h1>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat label="Contacts" value={stats?.contacts_total ?? "—"} sub={`${stats?.contacts_new ?? 0} new`} icon={Mail} />
          <Stat label="Bookings" value={stats?.bookings_total ?? "—"} sub={`${stats?.bookings_pending ?? 0} pending`} icon={CalendarDays} />
          <Stat label="Blog posts" value={stats?.blog_posts ?? "—"} sub="published & drafts" icon={FileText} />
          <Stat label="GTA visits" value={"On-demand"} sub="weekly office support" />
          <Stat label="Status" value="Live" sub="all systems normal" accent />
        </div>

        <div className="mt-10 flex gap-1 border-b border-border">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`px-5 py-3 text-sm font-semibold border-b-2 capitalize transition ${tab === t ? "border-[#16A34A] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "overview" && (
            <div className="grid lg:grid-cols-2 gap-6">
              <Panel title="Latest contacts">
                {contacts.slice(0, 5).map((c) => (
                  <Row key={c.id} title={c.name} sub={c.email} meta={c.status} />
                ))}
                {contacts.length === 0 && <div className="text-sm text-muted-foreground p-4">No contacts yet.</div>}
              </Panel>
              <Panel title="Latest bookings">
                {bookings.slice(0, 5).map((b) => (
                  <Row key={b.id} title={b.name} sub={`${b.preferred_date} · ${b.preferred_time}`} meta={b.status} />
                ))}
                {bookings.length === 0 && <div className="text-sm text-muted-foreground p-4">No bookings yet.</div>}
              </Panel>
            </div>
          )}

          {tab === "contacts" && (
            <Panel title="Contact submissions">
              <Table headers={["Name", "Email", "Service", "Message", "Status", "Actions"]}>
                {contacts.map((c) => (
                  <tr key={c.id} className="border-t border-border" data-testid={`contact-row-${c.id}`}>
                    <td className="p-3 font-semibold">{c.name}</td>
                    <td className="p-3 text-muted-foreground">{c.email}</td>
                    <td className="p-3 text-muted-foreground">{c.service || "—"}</td>
                    <td className="p-3 text-muted-foreground max-w-xs truncate">{c.message}</td>
                    <td className="p-3"><Badge value={c.status} /></td>
                    <td className="p-3">
                      <select value={c.status} onChange={(e) => updateContact(c.id, e.target.value)} className="bg-background border border-border rounded-md px-2 py-1 text-xs">
                        <option value="new">new</option>
                        <option value="in_progress">in_progress</option>
                        <option value="closed">closed</option>
                      </select>
                    </td>
                  </tr>
                ))}
                {contacts.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No contacts.</td></tr>}
              </Table>
            </Panel>
          )}

          {tab === "bookings" && (
            <Panel title="Consultation bookings">
              <Table headers={["Name", "Date", "Time", "Email", "Service", "Status", "Actions"]}>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-border" data-testid={`booking-row-${b.id}`}>
                    <td className="p-3 font-semibold">{b.name}</td>
                    <td className="p-3">{b.preferred_date}</td>
                    <td className="p-3">{b.preferred_time}</td>
                    <td className="p-3 text-muted-foreground">{b.email}</td>
                    <td className="p-3 text-muted-foreground">{b.service || "—"}</td>
                    <td className="p-3"><Badge value={b.status} /></td>
                    <td className="p-3">
                      <select value={b.status} onChange={(e) => updateBooking(b.id, e.target.value)} className="bg-background border border-border rounded-md px-2 py-1 text-xs">
                        <option value="pending">pending</option>
                        <option value="confirmed">confirmed</option>
                        <option value="completed">completed</option>
                        <option value="cancelled">cancelled</option>
                      </select>
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No bookings.</td></tr>}
              </Table>
            </Panel>
          )}

          {tab === "blog" && (
            <Panel
              title="Blog posts"
              action={<button onClick={() => setShowNew((v) => !v)} data-testid="new-post-btn" className="btn-accent !py-2 !px-4 text-sm"><Plus size={14}/> New post</button>}
            >
              {showNew && (
                <form onSubmit={createPost} className="p-6 border-b border-border space-y-4" data-testid="new-post-form">
                  <FInput label="Title" required value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                  <FInput label="Excerpt" required value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} />
                  <FInput label="Cover image URL" value={draft.cover_image} onChange={(e) => setDraft({ ...draft, cover_image: e.target.value })} />
                  <FInput label="Tags (comma-separated)" value={draft.tags} onChange={(e) => setDraft({ ...draft, tags: e.target.value })} />
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2 block">Content</label>
                    <textarea rows={8} required value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} className="w-full bg-background border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30" />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} />
                    Published
                  </label>
                  <button type="submit" className="btn-primary">Create post</button>
                </form>
              )}
              <Table headers={["Title", "Tags", "Status", "Created", "Actions"]}>
                {posts.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`post-row-${p.slug}`}>
                    <td className="p-3 font-semibold">{p.title}</td>
                    <td className="p-3 text-muted-foreground text-xs">{(p.tags || []).join(", ")}</td>
                    <td className="p-3"><Badge value={p.published ? "published" : "draft"} /></td>
                    <td className="p-3 text-muted-foreground text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="p-3">
                      <button onClick={() => deletePost(p.id)} className="text-destructive hover:underline text-xs inline-flex items-center gap-1"><Trash2 size={12}/> Delete</button>
                    </td>
                  </tr>
                ))}
                {posts.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No posts.</td></tr>}
              </Table>
            </Panel>
          )}
        </div>
      </div>
    </main>
  );
}

function Stat({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className={`card-apex p-5 ${accent ? "bg-[#0B2545] text-white border-[#0B2545]" : ""}`}>
      <div className="flex items-center justify-between">
        <div className={`text-xs uppercase tracking-widest font-bold ${accent ? "text-emerald-300" : "text-muted-foreground"}`}>{label}</div>
        {Icon && <Icon size={16} className={accent ? "text-emerald-300" : "text-[#16A34A]"} />}
      </div>
      <div className="mt-3 font-display font-black text-2xl lg:text-3xl">{value}</div>
      <div className={`mt-1 text-xs ${accent ? "text-white/60" : "text-muted-foreground"}`}>{sub}</div>
    </div>
  );
}

function Panel({ title, action, children }) {
  return (
    <div className="card-apex overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <h3 className="font-display font-bold text-lg">{title}</h3>
        {action}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Row({ title, sub, meta }) {
  return (
    <div className="px-6 py-3 border-b border-border last:border-0 flex items-center justify-between">
      <div>
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </div>
      <Badge value={meta} />
    </div>
  );
}

function Badge({ value }) {
  const color = value === "new" || value === "pending"
    ? "bg-[#16A34A]/10 text-[#16A34A]"
    : value === "confirmed" || value === "in_progress"
    ? "bg-blue-500/10 text-blue-600"
    : value === "closed" || value === "completed" || value === "published"
    ? "bg-slate-500/10 text-slate-600"
    : value === "cancelled" || value === "draft"
    ? "bg-red-500/10 text-red-600"
    : "bg-secondary text-foreground";
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${color}`}>{value}</span>;
}

function Table({ headers, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-secondary/50">
            {headers.map((h) => <th key={h} className="text-left p-3 text-xs uppercase tracking-widest text-muted-foreground font-bold">{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function FInput({ label, value, onChange, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2 block">{label}</label>
      <input required={required} value={value} onChange={onChange} className="w-full bg-background border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30" />
    </div>
  );
}
